import React, { useState, useMemo } from "react";
import {
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import ReportPdfLayout from "../../../Components/ReportPdfLayout";

const formatCurrency = (val) => {
  if (val == null || val === undefined) return "—";
  const n = Number(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PharmacyReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [reportType, setReportType] = useState("monthly");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.append("type", reportType);
    return p.toString();
  }, [reportType]);

  const endpoint = `pharmacy-reports/?${params}`;
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["pharmacy-report", params],
    endpoint,
    token
  );

  const handleApply = () => refetch();

  return (
    <Grid container maxWidth="xl" sx={{ p: 3, width: "100%" }}>
      <Grid item xs={12}>
        <ReportPdfLayout
          reportTitle={t("reports.pharmacyReport")}
          fileName="pharmacy-report.pdf"
          disabled={isLoading || isError || !data}
        >
          <Grid container>
            <Grid item xs={12} className="no-print">
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t("reports.pharmacyReport")}
              </Typography>
            </Grid>
            <Grid item xs={12} mb={2} className="no-print">
              <Paper sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("reports.reportType")}</InputLabel>
                  <Select
                    value={reportType}
                    label={t("reports.reportType")}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <MenuItem value="daily">{t("reports.daily")}</MenuItem>
                    <MenuItem value="weekly">{t("reports.weekly")}</MenuItem>
                    <MenuItem value="monthly">{t("reports.monthly")}</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={handleApply}>
                  {t("reports.apply")}
                </Button>
              </Paper>
            </Grid>
            {isError && (
              <Grid item xs={12}>
                <Alert severity="error">{error?.message || t("reports.errorLoading")}</Alert>
              </Grid>
            )}
            {isLoading && (
              <Grid item xs={12} display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Grid>
            )}
            {!isLoading && !isError && data && (
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reports.totalPrescriptions")}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {data.total_prescriptions ?? 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "success.main" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reports.totalPrescriptionAmount")}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(data.total_prescription_amount)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "info.main" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reports.totalMedicineOrderAmount")}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(data.total_medicine_order_amount)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Grid>
        </ReportPdfLayout>
      </Grid>
    </Grid>
  );
};

export default PharmacyReport;
